import { useEffect, useState } from 'react';
import AdminLayout from '../components/AdminLayout';
import { api } from '../../api';

const EMPTY = { title: '', url: '', year: new Date().getFullYear(), isLive: false };

export default function StreamControl() {
  const [streams, setStreams] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settingLive, setSettingLive] = useState(null);
  const [msg, setMsg] = useState('');

  const load = () => api.getStreams().then(setStreams).catch(console.error).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const flash = (m) => { setMsg(m); setTimeout(() => setMsg(''), 3500); };

  const startEdit = (s) => { setEditing(s.id); setForm({ title: s.title, url: s.url, year: s.year, isLive: s.isLive }); };
  const cancelEdit = () => { setEditing(null); setForm(EMPTY); };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) {
        await api.updateStream(editing, form);
        flash('Stream updated.');
      } else {
        await api.createStream(form);
        flash('Stream created.');
      }
      cancelEdit();
      load();
    } catch (err) {
      flash('Error: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const setLive = async (id) => {
    setSettingLive(id);
    try {
      await api.setLiveStream(id);
      load();
      flash('Stream is now live — all viewers will switch automatically.');
    } catch (err) {
      flash('Error: ' + err.message);
    } finally {
      setSettingLive(null);
    }
  };

  const del = async (id) => {
    if (!window.confirm('Delete this stream?')) return;
    try { await api.deleteStream(id); load(); flash('Deleted.'); } catch (err) { flash('Error: ' + err.message); }
  };

  return (
    <AdminLayout>
      <div className="admin-page-header">
        <h1>Live Stream Control</h1>
        <p>Set the active stream — viewers switch automatically via socket</p>
      </div>

      {msg && (
        <div style={{ background: 'rgba(212,170,106,.15)', border: '1px solid var(--a-gold2)', borderRadius: 8, padding: '10px 16px', marginBottom: 20, fontSize: 14, color: 'var(--a-gold)' }}>
          {msg}
        </div>
      )}

      <div className="a-card" style={{ marginBottom: 24 }}>
        <h3><i className="fas fa-plus-circle" style={{ marginInlineEnd: 8 }} />{editing ? 'Edit Stream' : 'Add Stream'}</h3>
        <form onSubmit={save}>
          <div className="a-form-grid">
            <div className="a-field-sm a-form-full">
              <label>Stream Title</label>
              <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. خطبة عرفة 1447هـ" required />
            </div>
            <div className="a-field-sm a-form-full">
              <label>Stream URL (HLS .m3u8)</label>
              <input value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} placeholder="https://..." required />
            </div>
            <div className="a-field-sm">
              <label>Year</label>
              <input type="number" value={form.year} onChange={(e) => setForm({ ...form, year: e.target.value })} required />
            </div>
          </div>
          <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
            <button className="a-btn-primary" type="submit" disabled={saving} style={{ width: 'auto', padding: '10px 28px' }}>
              {saving ? 'Saving…' : editing ? 'Update' : 'Create'}
            </button>
            {editing && (
              <button type="button" className="a-icon-btn" onClick={cancelEdit}>Cancel</button>
            )}
          </div>
        </form>
      </div>

      <div className="a-table-wrap">
        <div className="a-table-header">
          <h3>All Streams</h3>
          <p style={{ fontSize: 12, color: 'var(--a-muted)', margin: 0 }}>Click "Set Live" to switch — turns off all others and notifies all viewers instantly</p>
        </div>
        {loading ? (
          <div className="a-loader"><div className="a-spinner" /></div>
        ) : (
          <table className="a-table">
            <thead>
              <tr>
                <th>Title</th>
                <th>Year</th>
                <th>Status</th>
                <th>URL</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {streams.map((s) => (
                <tr key={s.id} style={s.isLive ? { background: 'rgba(34,197,94,.04)' } : {}}>
                  <td style={{ fontWeight: 600 }}>{s.title}</td>
                  <td>{s.year}</td>
                  <td>
                    {s.isLive ? (
                      <span className="a-badge green" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                        <span className="a-live-dot green" /> LIVE
                      </span>
                    ) : (
                      <span className="a-badge red">Offline</span>
                    )}
                  </td>
                  <td>
                    <span style={{ fontSize: 11, color: 'var(--a-muted)', wordBreak: 'break-all', maxWidth: 260, display: 'block' }}>
                      {s.url}
                    </span>
                  </td>
                  <td>
                    <div className="a-btn-row">
                      {!s.isLive && (
                        <button
                          className="a-icon-btn success"
                          onClick={() => setLive(s.id)}
                          disabled={settingLive === s.id}
                          title="Set as the live stream (turns off all others)"
                        >
                          <i className="fas fa-satellite-dish" />
                          {settingLive === s.id ? 'Setting…' : 'Set Live'}
                        </button>
                      )}
                      {s.isLive && (
                        <span style={{ fontSize: 12, color: '#22c55e', fontWeight: 600, padding: '4px 8px' }}>
                          <i className="fas fa-circle" style={{ fontSize: 8, marginInlineEnd: 6 }} />
                          Currently Live
                        </span>
                      )}
                      <button className="a-icon-btn" onClick={() => startEdit(s)} title="Edit">
                        <i className="fas fa-edit" />
                      </button>
                      <button className="a-icon-btn danger" onClick={() => del(s.id)} title="Delete">
                        <i className="fas fa-trash" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {streams.length === 0 && (
                <tr><td colSpan={5} style={{ textAlign: 'center', color: 'var(--a-muted)', padding: 32 }}>No streams yet.</td></tr>
              )}
            </tbody>
          </table>
        )}
      </div>
    </AdminLayout>
  );
}
